export type Footprint = readonly [number, number, number, number];
export type PolicyScheduleRegime = "R1" | "R2" | "R3";

export interface PolicyScheduleEvent {
  readonly time: number;
  readonly delta: Footprint;
  readonly label: string;
}

export interface PolicyTrajectoryPoint {
  readonly time: number;
  readonly footprint: Footprint;
  readonly kappa: number;
  readonly regime: PolicyScheduleRegime;
}

export interface PolicyTrajectory {
  readonly maxKappa: number;
  readonly points: readonly PolicyTrajectoryPoint[];
}

export const POLICY_SCHEDULE_EVENTS: readonly PolicyScheduleEvent[] = [
  { time: 1, delta: [1, 0, 0, 0], label: "Admit G1 bridge" },
  { time: 3, delta: [0, 1, 0, 0], label: "Admit G2 bridge" },
  { time: 5, delta: [-1, 0, 0, 0], label: "Revoke G1 bridge" },
];

export function footprintKappa(footprint: Footprint): number {
  return (
    2 *
    (footprint[0] * footprint[1] +
      footprint[0] * footprint[2] +
      footprint[0] * footprint[3] +
      footprint[1] * footprint[2] +
      footprint[1] * footprint[3] +
      footprint[2] * footprint[3])
  );
}

function applyFootprintDelta(footprint: Footprint, delta: Footprint): Footprint {
  return [
    Math.max(0, footprint[0] + delta[0]),
    Math.max(0, footprint[1] + delta[1]),
    Math.max(0, footprint[2] + delta[2]),
    Math.max(0, footprint[3] + delta[3]),
  ];
}

function regimeForFootprint(footprint: Footprint, kappa: number): PolicyScheduleRegime {
  const activeAxisCount = footprint.filter((value) => value > 0).length;
  if (activeAxisCount <= 1) return "R1";
  return kappa <= 20 ? "R2" : "R3";
}

function trajectoryPoint(time: number, footprint: Footprint): PolicyTrajectoryPoint {
  const kappa = footprintKappa(footprint);
  return {
    time,
    footprint,
    kappa,
    regime: regimeForFootprint(footprint, kappa),
  };
}

export function buildPolicyTrajectory(
  events: readonly PolicyScheduleEvent[],
): PolicyTrajectory {
  let footprint: Footprint = [0, 0, 0, 0];
  const points: PolicyTrajectoryPoint[] = [trajectoryPoint(0, footprint)];
  let maxKappa = 1;

  for (const event of [...events].sort((a, b) => a.time - b.time)) {
    footprint = applyFootprintDelta(footprint, event.delta);
    const point = trajectoryPoint(event.time, footprint);
    maxKappa = Math.max(maxKappa, point.kappa);
    points.push(point);
  }

  return { maxKappa, points };
}
